import { forwardRef } from 'react';
import PropTypes from 'prop-types';
// We enforce everywhere not to import 'next/link' so all links will go through
// this component. Thus we need to disable the lint rule in here.
// eslint-disable-next-line no-restricted-imports
import NextLink from 'next/link';
import { useRouter } from 'next/router';

import { TRACK_CATEGORIES } from 'lib/tracking';

import EventTrack from '../EventTrack/EventTrack';

/**
 * Using `React.forwardRef` to support rendering inside other components that
 * provide a ref prop (e.g. `ButtonLink`).
 *
 * @param {object} props
 */
const Link = forwardRef(function Link(
  { children, href, as, track, ...restProps },
  ref
) {
  const router = useRouter();
  const localeCode = router.query.locale;

  // If href is only "/", we remove it in order to prevent
  // a trailing slash on the locale.
  const linkHref = href === '/' ? '' : href;

  const linkAs = getLinkAs();

  function getLinkAs() {
    if (as) {
      return localeCode ? `/${localeCode}${as}` : as;
    }

    return localeCode ? `/${localeCode}${linkHref}` : null;
  }

  return (
    <NextLink
      href={
        href && localeCode && !href.startsWith('https')
          ? `/[locale]${linkHref}`
          : linkHref
      }
      as={href && !href.startsWith('https') ? linkAs : null}
      passHref
      ref={ref}
    >
      <EventTrack
        shouldTrack={Boolean(track)}
        track={{ category: TRACK_CATEGORIES.LINK_CLICK, ...track }}
      >
        <a {...restProps}>{children}</a>
      </EventTrack>
    </NextLink>
  );
});

Link.propTypes = {
  children: PropTypes.node.isRequired,
  /** The URL of the link. Which is the path inside pages directory */
  href: PropTypes.string.isRequired,
  /**
   * The path that will be rendered in the browser URL bar.
   * Used for dynamic routes.
   */
  as: PropTypes.string,
  /** Track object for analytics purposes */
  track: PropTypes.shape({
    event: PropTypes.string,
    category: PropTypes.string,
    label: PropTypes.string,
  }),
};

export default Link;
